import type { MapObject } from '../../../../map-editor-entities/schema/types'
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger'

/**
 * Check if a property key represents a relationship
 * Relationship keys typically end with: Id, Ids, Ref, Refs
 */
export function isRelationshipProperty(key: string): boolean {
  const relationPatterns = [
    /Id$/,      // zoneId, sensorId, controlId
    /Ids$/,     // columnIds, parkingLocationIds
    /Ref$/,     // assetRef
    /Refs$/     // assetRefs
  ]

  // Explicitly exclude known non-relationship properties
  const excludeList = ['name', 'description', 'serialNumber', 'ipAddress', 'model', 'resolution', 'powerOutput', 'chargingType', 'connectorType', 'status', 'type', 'angle', 'label', 'content', 'direction', 'floors', 'readerId', 'lightType', 'sensorType', 'range', 'spotNumber', 'spotType', 'zoneName', 'capacity', 'handicappedSpots', 'lineType', 'entranceType']

  if (excludeList.includes(key)) return false

  return relationPatterns.some(pattern => pattern.test(key))
}

/**
 * Extract relations from element properties
 */
export function extractRelations(properties: Record<string, any>) {
  const relations: Array<{ targetId: string; type: 'required' | 'optional' | 'reference'; meta?: Record<string, any> }> = []

  log.debug(LogCategory.RELATIONSHIP, 'Extracting relations from properties', {
    propertyKeys: Object.keys(properties),
  })

  Object.entries(properties).forEach(([key, value]) => {
    // Only process relationship properties
    if (!isRelationshipProperty(key)) {
      return
    }

    // Handle single ID (string)
    if (typeof value === 'string' && value.length > 0) {
      log.debug(LogCategory.RELATIONSHIP, `Found single relation: ${key} = ${value}`)
      relations.push({
        targetId: value,
        type: 'reference',
        meta: { propertyKey: key }
      })
      return
    }

    // Handle single ID (number - convert to string)
    if (typeof value === 'number') {
      const stringId = String(value)
      log.debug(LogCategory.RELATIONSHIP, `Found single relation (numeric): ${key} = ${stringId}`)
      relations.push({
        targetId: stringId,
        type: 'reference',
        meta: { propertyKey: key }
      })
      return
    }

    // Handle multiple IDs (array)
    if (Array.isArray(value) && value.length > 0) {
      // Filter to only valid string IDs (handles strings and numbers)
      const validIds = value
        .filter(id => typeof id === 'string' || typeof id === 'number')
        .map(id => String(id))
        .filter(id => id.length > 0)

      if (validIds.length > 0) {
        log.debug(LogCategory.RELATIONSHIP, `Found multi relation: ${key}`, {
          validCount: validIds.length,
          totalCount: value.length,
        })
        validIds.forEach(targetId => {
          relations.push({
            targetId,
            type: 'reference',
            meta: { propertyKey: key }
          })
        })
      } else {
        log.debug(LogCategory.RELATIONSHIP, `Skipping ${key} - array contains no valid IDs`, {
          invalidCount: value.length,
        })
      }
      return
    }

    // Log skip for other cases
    log.debug(LogCategory.RELATIONSHIP, `Skipping ${key} - empty or invalid value`, value)
  })

  log.info(LogCategory.RELATIONSHIP, `Extracted ${relations.length} relations`)
  return relations.length > 0 ? relations : undefined
}

/**
 * Process a relationship value, handling arrays and single values
 * - Filters out null, undefined, empty strings, and objects
 * - Converts numbers to strings
 * - Deduplicates array values
 * - Returns undefined for empty arrays
 */
export function processRelationshipValue(value: any): any {
  // Handle arrays
  if (Array.isArray(value)) {
    const processed = value
      .filter(item => {
        // Filter out null, undefined, empty strings
        if (item === null || item === undefined || item === '') {
          return false
        }
        // Filter out objects (like {})
        if (typeof item === 'object') {
          return false
        }
        return true
      })
      .map(item => String(item)) // Convert all to strings
      .filter((value, index, self) => self.indexOf(value) === index) // Deduplicate

    // Return undefined if array is empty after processing
    return processed.length > 0 ? processed : undefined
  }

  // Handle single values
  if (value === '' || value === null || value === undefined) {
    return undefined
  }

  // Convert numbers to strings
  if (typeof value === 'number') {
    return String(value)
  }

  return value
}

/**
 * Extract ALL relationship properties from object properties
 * This includes both original properties and derived relations
 */
export function extractAllRelationProperties(object: MapObject): Record<string, any> {
  const result: Record<string, any> = {}

  // Include all relationship properties from the original properties
  if (object.properties) {
    Object.entries(object.properties).forEach(([key, value]) => {
      if (isRelationshipProperty(key)) {
        let processed = processRelationshipValue(value)
        // 구역 이름은 '구역' 접미사 없이 번호만 내보내므로(transformToZone),
        // 이를 가리키는 zoneId 참조도 같이 벗겨야 어긋나지 않는다.
        if (key === 'zoneId' && typeof processed === 'string') {
          processed = processed.replace(/\s*구역$/, '')
        }
        if (processed !== undefined) {
          result[key] = processed
        }
      }
    })
  }

  return result
}
